import {
  CONTACT_EXERCISE_FRAME,
  DEPARTMENTS,
  INSURANCE_PLAN_PARAMS_RULES,
  JOB_LIST,
  PROFILE_LIST,
  SOCIAL_REGIMES,
} from "utils/constants";
import { get } from "lodash";

const fromMap = (map) =>
  Object.entries(map).map(([key, value]) => ({
    value,
    label: key.replace(/_/g, " "),
  }));

export const ELIGIBILITY_DIMENSIONS = [
  {
    dimension: "age",
    label: "Tranche d'âge",
    widget: "numberRange",
    bounds: [
      {
        predicate: "gte",
        legacyRule: INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
        addon: "Min",
      },
      {
        predicate: "lte",
        legacyRule: INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
        addon: "Max",
      },
    ],
  },
  {
    dimension: "regime",
    label: "Régimes sociaux",
    widget: "multiselect",
    predicate: "includes",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES,
    placeholder: "Sélectionner les régimes...",
    options: fromMap(SOCIAL_REGIMES),
  },
  {
    dimension: "exerciseFrame",
    label: "Cadre d'exercice",
    widget: "select",
    predicate: "eq",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.EXERCISE_FRAME,
    placeholder: "Tous",
    options: [
      { value: CONTACT_EXERCISE_FRAME.EMPLOYEE, label: "Salarié" },
      { value: CONTACT_EXERCISE_FRAME.INDEPENDENT, label: "Indépendant" },
    ],
  },
  {
    dimension: "profession",
    label: "Professions",
    widget: "multiselect",
    predicate: "includes",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
    placeholder: "Sélectionner les professions...",
    options: fromMap(JOB_LIST),
  },
  {
    dimension: "profile",
    label: "Profils",
    widget: "multiselect",
    predicate: "includes",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.PROFILES,
    placeholder: "Sélectionner les profils...",
    options: fromMap(PROFILE_LIST).sort((a, b) =>
      a.label.localeCompare(b.label),
    ),
  },
  {
    dimension: "department",
    label: "Départements",
    widget: "multiselect",
    predicate: "includes",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS,
    placeholder: "Sélectionner les départements...",
    showSearch: true,
    options: DEPARTMENTS.map((value) => ({ value, label: value })),
  },
  {
    dimension: "zipCode",
    label: "Codes postaux",
    widget: "tags",
    predicate: "includes",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES,
    placeholder: "Saisir les codes postaux...",
    tagPattern: /^\d{5}$/,
    tagError: "Zip codes must be 5 digits",
  },
  {
    dimension: "idcc",
    labelKey: "FORM.CONDITIONS.IDCC.LABEL",
    widget: "idcc",
    predicate: "includes",
    legacyRule: INSURANCE_PLAN_PARAMS_RULES.IDCC,
    placeholderKey: "FORM.CONDITIONS.IDCC.PLACEHOLDER",
    enabled: false,
  },
];

export const ENABLED_DIMENSIONS = ELIGIBILITY_DIMENSIONS.filter(
  (dimension) => dimension.enabled !== false,
);

export const slotKey = (name, predicate) =>
  predicate ? `${name}::${predicate}` : name;

const LEGACY_TO_NATIVE = ELIGIBILITY_DIMENSIONS.reduce((acc, dim) => {
  if (dim.bounds) {
    for (const bound of dim.bounds)
      acc[bound.legacyRule] = {
        dimension: dim.dimension,
        predicate: bound.predicate,
      };
    return acc;
  }
  if (dim.legacyRule)
    acc[dim.legacyRule] = {
      dimension: dim.dimension,
      predicate: dim.predicate,
    };
  return acc;
}, {});

export const rulesToSlots = (rules) =>
  (rules || []).reduce((acc, rule) => {
    if (rule.predicate) {
      acc[slotKey(rule.name, rule.predicate)] = {
        id: rule.id,
        name: rule.name,
        predicate: rule.predicate,
        required: rule.required,
        value: rule.value,
      };
      return acc;
    }
    const native = get(LEGACY_TO_NATIVE, rule.name);
    if (native) {
      acc[slotKey(native.dimension, native.predicate)] = {
        id: rule.id,
        name: native.dimension,
        predicate: native.predicate,
        required: rule.required,
        value: rule.value,
      };
      return acc;
    }
    acc[slotKey(rule.name)] = {
      id: rule.id,
      name: rule.name,
      value: rule.value,
    };
    return acc;
  }, {});

export const slotsToRules = (slots) =>
  Object.values(slots || {})
    .filter((rule) => rule && rule.name)
    .map((rule) => {
      const out = { id: rule.id, name: rule.name, value: rule.value };
      if (rule.predicate) out.predicate = rule.predicate;
      if (rule.required) out.required = rule.required;
      return out;
    });
